import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import SystemHeader from '../SystemHeader';
import GuideButton from '../shared/GuideButton';
import { ScoresAPI } from '../../services/feature-api';

const accent = '#f59e0b';

const CATEGORY_COLORS = {
  'reels-auto-check': { bg: '#f5f3ff', color: '#9C27B0', icon: '🎬' },
  'reel-trending': { bg: '#fdf2f8', color: '#ec4899', icon: '🔥' },
  livestream: { bg: '#faf5ff', color: '#8b5cf6', icon: '📺' },
  'team-meeting': { bg: '#fff7ed', color: '#ea580c', icon: '🤝' },
  market: { bg: '#f0fdf4', color: '#22c55e', icon: '🏪' },
  'spa-market': { bg: '#fce7f3', color: '#db2777', icon: '💆' },
  'daily-report': { bg: '#eff6ff', color: '#3b82f6', icon: '📋' },
  inventory: { bg: '#fef3c7', color: '#f59e0b', icon: '📦' },
  'retail-order': { bg: '#ecfdf5', color: '#10b981', icon: '🛒' },
  'wholesale-order': { bg: '#f0fdfa', color: '#14b8a6', icon: '📊' },
  'toggle-sales': { bg: '#fef9c3', color: '#ca8a04', icon: '💰' },
};

const ScorePage = ({ userInfo, accessToken, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const [scoreData, setScoreData] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [month] = useState(() => new Date().getMonth() + 1);
  const [year] = useState(() => new Date().getFullYear());

  const loadScores = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const result = await ScoresAPI.getScores(accessToken, month, year);
      if (result.success && result.data) {
        setTotalScore(result.data.totalScore || 0);
        // Map backend categories to display format
        const categories = (result.data.categories || []).map(cat => ({
          id: cat.key,
          name: cat.label,
          source: cat.key,
          totalPoints: cat.score,
          maxPoints: cat.maxScore,
          matchCount: cat.details?.count ?? 0,
          maxMonth: cat.maxScore,
          description: cat.details?.rule || '',
          points: cat.score,
          matchedItems: [],
          capped: false,
          rawCount: cat.details?.count || 0,
        }));
        setScoreData(categories);
      }
    } catch (e) { console.warn('Failed to load scores:', e); }
    setLoading(false);
  }, [accessToken, month, year]);

  useEffect(() => { loadScores(); }, [loadScores]);

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Điểm số" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Back + title */}
        <div className="flex items-center gap-2 px-3 pt-2 pb-1 bg-white">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ color: accent, background: accent + '18' }}>
            <ArrowLeft size={14} /> Quay lại
          </button>
          <GuideButton slug="co-che-thuong" />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
          <h1 className="text-sm font-bold" style={{ color: accent }}>🏆 Bảng điểm tháng {month}/{year}</h1>
        </div>

        {/* Total score card */}
        <div className="mx-3 mt-3 rounded-2xl p-5 text-center shadow-md" style={{ background: 'linear-gradient(135deg, #f59e0b, #eab308)' }}>
          <Trophy size={32} className="mx-auto mb-2 text-white/90" />
          <div className="text-4xl font-black text-white">{loading ? '...' : totalScore}</div>
          <div className="text-white/80 text-sm font-semibold mt-1">Tổng điểm tháng này</div>
        </div>

        {/* Score breakdown */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent }} />
          </div>
        ) : (
          <div className="px-3 py-3 space-y-2">
            {/* Summary chips */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {scoreData.filter(d => d.totalPoints > 0).map(d => {
                const cat = CATEGORY_COLORS[d.source] || { bg: '#f3f4f6', color: '#6b7280', icon: '📌' };
                return (
                  <div key={d.id} className="rounded-xl py-2 px-2 text-center" style={{ background: cat.bg }}>
                    <div className="text-lg font-black" style={{ color: cat.color }}>{d.totalPoints}</div>
                    <div className="text-[9px] font-semibold" style={{ color: cat.color + 'cc' }}>{d.name}</div>
                  </div>
                );
              })}
            </div>

            {/* Detail list */}
            {scoreData.map(d => {
              const cat = CATEGORY_COLORS[d.source] || { bg: '#f3f4f6', color: '#6b7280', icon: '📌' };
              const isOpen = expanded[d.id];
              return (
                <div key={d.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                  <button onClick={() => toggleExpand(d.id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ background: cat.bg }}>{cat.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-800">{d.name}</div>
                      <div className="text-[10px] text-gray-400">{d.description}</div>
                    </div>
                    <div className="text-right flex-shrink-0 mr-1">
                      <div className="text-sm font-black" style={{ color: d.totalPoints > 0 ? cat.color : '#d1d5db' }}>{d.totalPoints}</div>
                      <div className="text-[9px] text-gray-400">{d.matchCount}{d.maxMonth > 0 ? `/${d.maxMonth}` : ''}</div>
                    </div>
                    {isOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </button>
                  {isOpen && (
                    <div className="border-t border-gray-100 px-3 py-2 bg-gray-50/50">
                      {/* Progress bar */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${d.maxPoints > 0 ? Math.min(100, (d.totalPoints / d.maxPoints) * 100) : (d.totalPoints > 0 ? 100 : 0)}%`, background: cat.color }} />
                        </div>
                        <span className="text-[10px] font-bold" style={{ color: cat.color }}>{d.totalPoints}{d.maxPoints > 0 ? `/${d.maxPoints}` : ''}</span>
                      </div>
                      {d.description ? (
                        <div className="text-xs text-gray-600 py-1">
                          <span className="text-gray-400">Quy tắc:</span> {d.description}
                        </div>
                      ) : (
                        <div className="text-center text-xs text-gray-400 py-2">Chưa có dữ liệu tháng này</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScorePage;
